import React, { useMemo, useState } from 'react';
import { FiCheck as Check, FiCopy as Copy } from 'react-icons/fi';
import Modal from '../ui/Modal';

interface CodeSnippetModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  language?: string;
  code?: string | null;
  subtitle?: React.ReactNode | null;
}

const CodeSnippetModal = ({
  isOpen,
  onClose,
  title = 'Details',
  language = 'python',
  code = '',
  subtitle = null,
}: CodeSnippetModalProps) => {
  const [copied, setCopied] = useState<boolean>(false);

  const safeCode = useMemo(() => (code == null ? '' : String(code)), [code]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(safeCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err: unknown) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="max-w-5xl">
      {subtitle && (
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 break-all">
          {subtitle}
        </p>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {language ? `Language: ${language}` : null}
        </div>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-900 text-gray-100 rounded-md hover:bg-gray-800"
          title="Copy to clipboard"
        >
          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          <span className="text-sm">{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>

      <div className="bg-gray-900 dark:bg-gray-950 rounded-lg p-4 border border-gray-700 overflow-x-auto max-h-[70vh] overflow-y-auto">
        <pre className="text-xs text-gray-100 font-mono whitespace-pre">
          {safeCode || '—'}
        </pre>
      </div>
    </Modal>
  );
};

export default CodeSnippetModal;
